"use client";

import { isDomCircularJsonSerializationError } from "@/components/error/domSerializationError";
import { mapRecord, notNull } from "@/utils/helpers";
import { isIgnoredBrowserError } from "@/utils/logging/ignoredBrowserErrors";
import { registerIsomorphicLogger } from "@/utils/logging/isomorphic";
import { redactTokenPathSegments } from "@/utils/logging/redact";
import { captureInSentry } from "@/utils/o11y/sentry/capture";
import { errorPlaceTag } from "@/utils/o11y/tags";
import { getContextAttributes } from "@/utils/tracing/client";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import type { RumEvent } from "@datadog/browser-rum";
import { createLogger, writeJsonToConsole } from "@listen-labs/o11y/logs";
import type {
  LogLevel,
  LogMeta,
  LogRecord,
  Logger,
} from "@listen-labs/o11y/logs";

export type { LogMeta, Logger, LogLevel } from "@listen-labs/o11y/logs";

// The Browser Logs SDK touches `window`/`document` and throws during SSR.
const isBrowser = typeof window !== "undefined";

// Error Tracking needs `error.stack`, which the SDK computes only for Error instances.
// Only these primitive fields are copied from a non-Error object: it can be a raw provider payload.
const SAFE_ERROR_FIELDS = ["name", "message", "code", "status", "type"];

const toTrackableError = (
  value: unknown
): { error: Error; details: LogMeta } => {
  if (typeof value !== "object" || value === null) {
    return { error: new Error(String(value)), details: {} };
  }
  const details = Object.fromEntries(
    SAFE_ERROR_FIELDS.flatMap((key) => {
      const field: unknown = Reflect.get(value, key);
      return ["string", "number", "boolean"].includes(typeof field)
        ? [[key, field]]
        : [];
    })
  );
  const error = new Error(
    typeof details.message === "string" ? details.message : "Non-Error object"
  );
  if (typeof details.name === "string") error.name = details.name;
  return { error, details: { error_details: details } };
};

const toTrackable = (error: unknown): { error?: Error; details: LogMeta } => {
  if (error instanceof Error) return { error, details: {} };
  if (!notNull(error)) return { details: {} };
  return toTrackableError(error);
};

// RUM drops events once its session has expired (an idle tab), so the log keeps the Error then.
const isRumSessionActive = (): boolean =>
  isBrowser && notNull(datadogRum.getInternalContext()?.session_id);

const writeToDatadog = ({ level, message, error, meta }: LogRecord) => {
  const trackable = toTrackable(error);
  const fields = { ...meta, ...trackable.details };
  const isCapture = level === "error" && meta.captured === true;
  const isIgnoredCapture =
    isCapture &&
    notNull(trackable.error) &&
    isIgnoredBrowserError(trackable.error);
  if (isIgnoredCapture) return;
  if (isCapture && notNull(trackable.error) && isRumSessionActive()) {
    // RUM owns captured browser errors once it runs: one Error Tracking issue per error, with its session and view.
    // The log record keeps the message only, so Error Tracking does not file it a second time.
    datadogRum.addError(trackable.error, { ...fields, log_message: message });
    datadogLogs.logger.log(
      message,
      { ...fields, error_message: trackable.error.message },
      level
    );
    return;
  }
  datadogLogs.logger.log(message, fields, level, trackable.error);
};

export const createBrowserLogger = ({
  minimumLevel = "info",
}: { minimumLevel?: LogLevel } = {}): Logger =>
  createLogger({
    minimumLevel,
    getContext: getContextAttributes,
    write: isBrowser ? writeToDatadog : writeJsonToConsole,
    capture: captureInSentry,
  });

export const logger = createBrowserLogger();
registerIsomorphicLogger(logger);

// Query strings carry magic-link hashes and share tokens; the SDKs attach the page URL to every event.
export const redactBrowserUrl = (url: string): string =>
  redactTokenPathSegments(url.split(/[?#]/)[0]);

// The fields this redacts, on whichever RUM event carries them.
type RedactableRumEvent = {
  type: RumEvent["type"];
  view?: {
    url: string;
    referrer?: string;
    // The largest contentful element can be a private, signed media URL.
    performance?: { lcp?: { resource_url?: string } };
  };
  resource?: { url: string };
  error?: {
    source: string;
    type?: string;
    message?: string;
    stack?: string;
    causes?: { message?: string; stack?: string }[];
    resource?: { url: string };
  };
  long_task?: { scripts?: { source_url?: string }[] };
  // The attributes addError was given: log_message, error_details, and capture attributes.
  context?: Record<string, unknown>;
};

// Error text can also quote a root-relative path, which carries the same tokens and query strings.
const ROOT_RELATIVE_PATH = /(^|[\s(['"])(\/[\w./%-]+(?:\?[^\s)\]'"]*)?)/g;

// Failed-request messages and stacks quote the request URL, query string included.
// Stack frames end in `:line:col`, which must survive the redaction.
const redactUrlsInText = (text: string): string =>
  text
    .replace(
      /(https?:\/\/[^\s)'"]+?)((?::\d+){1,2})?(?=[\s)'"]|$)/g,
      (_match, url: string, position: string | undefined) =>
        `${redactBrowserUrl(url)}${position ?? ""}`
    )
    .replace(
      ROOT_RELATIVE_PATH,
      (_match, lead: string, path: string) => `${lead}${redactBrowserUrl(path)}`
    );

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const redactUrlsInValue = (value: unknown): unknown => {
  if (typeof value === "string") return redactUrlsInText(value);
  if (Array.isArray(value)) return value.map(redactUrlsInValue);
  if (isRecord(value)) return redactUrlsInContext(value);
  return value;
};

const redactUrlsInContext = (
  context: Record<string, unknown>
): Record<string, unknown> =>
  mapRecord(context, (_key, value) => redactUrlsInValue(value));

// RUM also collects uncaught errors and console.error calls itself; the logger's handlers already report those.
const RUM_ERROR_SOURCES_REPORTED_BY_LOGGER = new Set(["source", "console"]);

/** RUM `beforeSend`: drops the errors the logger reports or ignores, and redacts the URLs RUM attaches. */
export const prepareRumEvent = (event: RedactableRumEvent): boolean => {
  if (event.type === "error" && notNull(event.error)) {
    const isReportedByLogger = RUM_ERROR_SOURCES_REPORTED_BY_LOGGER.has(
      event.error.source
    );
    const isIgnored = isIgnoredBrowserError({
      name: event.error.type,
      message: event.error.message,
    });
    if (isReportedByLogger || isIgnored) return false;
  }
  if (notNull(event.view)) {
    event.view.url = redactBrowserUrl(event.view.url);
    if (notNull(event.view.referrer)) {
      event.view.referrer = redactBrowserUrl(event.view.referrer);
    }
    const lcp = event.view.performance?.lcp;
    if (notNull(lcp?.resource_url)) {
      lcp.resource_url = redactBrowserUrl(lcp.resource_url);
    }
  }
  event.long_task?.scripts?.forEach((script) => {
    if (notNull(script.source_url)) {
      script.source_url = redactBrowserUrl(script.source_url);
    }
  });
  if (notNull(event.resource)) {
    event.resource.url = redactBrowserUrl(event.resource.url);
  }
  if (notNull(event.error?.resource)) {
    event.error.resource.url = redactBrowserUrl(event.error.resource.url);
  }
  if (notNull(event.error?.message)) {
    event.error.message = redactUrlsInText(event.error.message);
  }
  if (notNull(event.error?.stack)) {
    event.error.stack = redactUrlsInText(event.error.stack);
  }
  event.error?.causes?.forEach((cause) => {
    if (notNull(cause.message)) cause.message = redactUrlsInText(cause.message);
    if (notNull(cause.stack)) cause.stack = redactUrlsInText(cause.stack);
  });
  if (notNull(event.context)) {
    event.context = redactUrlsInContext(event.context);
  }
  return true;
};

export type UncaughtBrowserErrorPlace = "window_error" | "unhandled_rejection";

// Third-party scripts stringifying React-managed DOM nodes; tracked as a metric by global-error.tsx instead.
export const captureUncaughtBrowserError = (
  error: unknown,
  place: UncaughtBrowserErrorPlace
): void => {
  if (isDomCircularJsonSerializationError(error)) return;
  logger.captureException(error, errorPlaceTag.withValue(place));
};

// A cross-origin script error has no `.error`, but the event still carries its source location.
const errorFromEvent = ({
  error,
  message,
  filename,
  lineno,
  colno,
}: ErrorEvent): unknown => {
  if (notNull(error) || !filename) return error ?? message;
  const located = new Error(message);
  located.stack = `Error: ${message}\n    at <anonymous> (${redactBrowserUrl(filename)}:${lineno}:${colno})`;
  return located;
};

/** The logger owns uncaught browser errors; Sentry's GlobalHandlers integration stays off so each is one event. */
export const captureBrowserErrors = (): void => {
  window.addEventListener("error", (event) =>
    captureUncaughtBrowserError(errorFromEvent(event), "window_error")
  );
  window.addEventListener("unhandledrejection", ({ reason }) =>
    captureUncaughtBrowserError(reason, "unhandled_rejection")
  );
};
